import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../../../../../../AppStores";
import type {App} from "../../../../../../../../../App";
import type {AppState} from "../../../../../../../../../data/state/AppState";
import {Field} from "../../../../../../../../widgets/form/field/Field";
import {SelectInput} from "../../../../../../../../widgets/input/select/SelectInput";
import {ReactUtils} from "../../../../../../../../utils/ReactUtils";
import {IconButton} from "../../../../../../../../widgets/button/IconButton";
import {SingleLineInput} from "../../../../../../../../widgets/input/clicktoedit/datatypes/singleline/SingleLineInput";
import {ConfirmWindow} from "../../../../../../popups/ConfirmWindow";
import type {IResponse} from "../../../../../../../../../data/TransportLayer";
import type {IMassInputProps, IOption} from "../IMassInput";
import {massUpdatePressButton, getItemsToCheck, getMassUpdateOptions} from "../IMassInput";
import type {TransformObj} from "../../../../../../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../../../../../../utils/dom/DomUtils";
import {FocusLoss} from "../../../../../../../../../utils/ui/focus/FocusLoss";
import {TimeUtils} from "../../../../../../../../../utils/TimeUtils";
import {DomPortal} from "../../../../../../portal/DomPortal";
import {AppUtils} from "../../../../../../../../../utils/AppUtils";
import {FieldInputs} from "../../../../../../../../widgets/input/clicktoedit/InputUtils";
import type {IModel} from "../../../../../../../../../data/models/Model";
import {KeyboardListener} from "../../../../../../../../../utils/interaction/key/KeyboardListener";
import {Permission} from "../../../../../../../../../generated/api/base";

interface IMassFieldMultiLineInputProps extends IMassInputProps {
	readonly noFixedPosition?: boolean;
	readonly app?: App;
	readonly appState?: AppState;
}

interface IMassFieldMultiLineInputState {
	selectedOption: string;
	inputValue: string;
	updating: boolean;
	transform: TransformObj;
	open: boolean;
}

@withAppStores("app", "appState")
@observer
export class MassFieldMultiLineInput extends React.Component<IMassFieldMultiLineInputProps, IMassFieldMultiLineInputState> {
	private readonly _options: IOption[];
	private _element = React.createRef<HTMLDivElement>();
	private _floating = React.createRef<HTMLDivElement>();

	constructor(props: IMassFieldMultiLineInputProps) {
		super(props);
		this.state = {
			selectedOption: "replace",
			inputValue: "",
			updating: false,
			transform: null,
			open: this.props.open,
		};

		this._options = [
			{
				id: "append",
				label: "Append Text",
			},
			{
				id: "replace",
				label: "Replace Text",
			},
		];
	}

	static getDerivedStateFromProps(props: IMassFieldMultiLineInputProps, state: IMassFieldMultiLineInputState) {
		if (props.open !== state.open) {
			return {open: props.open};
		}

		return null;
	}

	private onOpenClick = async () => {
		const {onOpen, field} = this.props;

		onOpen(field.refId);
		await TimeUtils.wait(100);
		this.addListeners();
	};

	private onApply = async (items: IModel[]) => {
		const {field, appState, onOpen, app} = this.props;
		const {selectedOption, inputValue} = this.state;

		KeyboardListener.getInstance().signals.up.remove(this.pressButton);

		const itemsToUpdate = items.filter(
			(item) => appState.actions.getFieldPermission(field, [item]) > Permission.View && appState.actions.hasOwnFieldValue(item, field.refId),
		);

		const confirmed = await ConfirmWindow.open(`${itemsToUpdate.length} of ${items.length} items will be updated. Continue?`, "Confirm Update");

		if (confirmed) {
			onOpen("");
			this.removeListeners();
			this.setState({updating: true});
			if (selectedOption === "append") {
				const promises: Promise<IResponse>[] = [];

				for (const item of itemsToUpdate) {
					let value = (appState.actions.getOwnFieldValue(item, field.refId) || "") as string;

					value += inputValue;
					promises.push(
						appState.actions.updateFields([item], {
							[field.refId]: value,
						}),
					);
				}

				await TimeUtils.waitUpdates(promises, itemsToUpdate, app.notificationContainer);
			} else if (selectedOption === "replace") {
				await TimeUtils.waitUpdate(
					appState.actions.updateMassFields(itemsToUpdate, {
						[field.refId]: inputValue,
					}),
					app.notificationContainer,
				);
			}
			this.setState({updating: false, selectedOption: "replace", inputValue: ""});
		} else {
			KeyboardListener.getInstance().signals.up.add(this.pressButton);
		}
	};

	private onBlur = (event?: MouseEvent) => {
		if (!(event?.target instanceof Element) || !this.props.app.modalContainer.contains(event?.target)) {
			this.onCancel();
		} else {
			FocusLoss.stopListen(this._floating.current, this.onBlur);
			FocusLoss.listen(this._floating.current, this.onBlur);
		}
	};

	private onCancel = () => {
		this.removeListeners();
		this.props.onOpen("");
	};

	private onOptionChange = (option: IOption) => {
		this.setState({
			selectedOption: option.id,
		});
	};

	private onInputChange = (value: any) => {
		this.setState({
			inputValue: value,
		});
	};

	private removeListeners = () => {
		AppUtils.disableScrolling(false);
		FocusLoss.stopListen(this._floating.current, this.onBlur);
		KeyboardListener.getInstance().signals.up.remove(this.pressButton);
	};

	private addListeners = () => {
		FocusLoss.stopListen(this._floating.current, this.onBlur);
		FocusLoss.listen(this._floating.current, this.onBlur);
		KeyboardListener.getInstance().signals.up.remove(this.pressButton);
		KeyboardListener.getInstance().signals.up.add(this.pressButton);
		AppUtils.disableScrolling(true);
	};

	private pressButton = (event: KeyboardEvent) => {
		if (!event.shiftKey) {
			massUpdatePressButton(event, true, this.onApply, this.onCancel, this.props);
		}
	};

	private getEditInput() {
		const {field} = this.props;
		const {inputValue} = this.state;

		let InputComponent = FieldInputs[field.dataType];

		InputComponent = InputComponent || SingleLineInput;

		return (
			<InputComponent
				value={inputValue}
				dataTypeSettings={field.dataTypeSettings}
				onChange={this.onInputChange}
				onInput={this.onInputChange}
			/>
		);
	}

	public override componentDidUpdate(prevProps: IMassFieldMultiLineInputProps, prevState: IMassFieldMultiLineInputState) {
		if (!this.props.noFixedPosition && !prevState.open && this.state.open && this._element.current && this._floating.current) {
			this.setState({
				transform: DomUtils.getFixedFloatingElementPosition(
					this._element.current,
					this._floating.current,
					VerticalAlignment.bottom,
					HorizontalAlignment.left,
					10,
				),
			});
		}
	}

	public override componentWillUnmount() {
		this.removeListeners();
	}

	public override render() {
		const {open, items, field, appState, noFixedPosition, disabled} = this.props;
		const {selectedOption, updating, transform} = this.state;
		const element = this._element;
		const itemsToCheck = getItemsToCheck(items, field, appState);
		const {allValuesMatch, firstValue} = getMassUpdateOptions(itemsToCheck, field, appState);

		let inlineStyle: React.CSSProperties = element && {
			transform: transform?.translate,
			position: noFixedPosition ? "absolute" : "fixed",
		};

		if (noFixedPosition && inlineStyle) {
			inlineStyle.transform = "";
		}

		return (
			<div className={ReactUtils.cls("MassFieldInput", {open})}>
				<Field
					key={field.refId}
					label={field.name}
					noWrap={true}
					disabled={disabled}
				>
					<div
						className="unfocused"
						onClick={this.onOpenClick}
						ref={this._element}
					>
						{allValuesMatch ? firstValue : <i>Multiple values</i>}
						{updating && <span className="spinner" />}
					</div>
					{open && (
						<DomPortal
							destination={this.props.app.modalContainer}
							noPortal={noFixedPosition}
						>
							<div
								className={ReactUtils.cls("MassFieldInput__editArea", {[VerticalAlignment[transform?.vertical]]: transform})}
								style={inlineStyle}
								ref={this._floating}
							>
								<div className="header hbox alignCenter">
									<h4 className="flex_1">
										Editing multiple Values for <span>{`${this.props.field.name}`}</span>
									</h4>
									<IconButton
										icon="close"
										onClick={this.onCancel}
									/>
								</div>
								<div className="container">
									<Field label="Action">
										<SelectInput
											options={this._options}
											render={(option) => option.label}
											selected={this._options.find((option) => option.id === selectedOption)}
											onChange={this.onOptionChange}
										/>
									</Field>
									<Field label="Value">{this.getEditInput()}</Field>
									<div className="editButtons">
										<IconButton
											className="apply"
											icon="apply"
											title="Apply"
											onClick={() => this.onApply(itemsToCheck)}
										/>
										<IconButton
											className="cancel"
											icon="cancel"
											title="Cancel"
											onClick={this.onCancel}
										/>
									</div>
								</div>
							</div>
						</DomPortal>
					)}
				</Field>
			</div>
		);
	}
}
